import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Gift,
  Info,
  Loader2,
} from "lucide-react";
import { ProtectionChecklist } from "./ProtectionChecklist";
import { calculateSellerPayout, SellerPayoutBreakdown } from "@/lib/cashfree/paymentService";

const formatMoney = (value: number) =>
  value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface SellerPaymentRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (amount: string) => Promise<void>;
  sellerId: string;
  dealId?: string; // Optional - kept for future use
}

export function SellerPaymentRequestModal({
  isOpen,
  onClose,
  onSubmit,
  sellerId,
  dealId,
}: SellerPaymentRequestModalProps) {
  const [amount, setAmount] = useState("");
  const [breakdown, setBreakdown] = useState<SellerPayoutBreakdown | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showProtection, setShowProtection] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const numericAmount = Number(amount);
  const isValidAmount = Number.isFinite(numericAmount) && numericAmount >= 100;

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setAmount("");
      setBreakdown(null);
      setShowProtection(false);
      setShowBreakdown(false);
      setError(null);
    }
  }, [isOpen]);

  // Calculate breakdown when amount changes
  useEffect(() => {
    const calculateBreakdown = async () => {
      if (!amount || !/^\d*\.?\d*$/.test(amount)) {
        setBreakdown(null);
        setShowProtection(false);
        setShowBreakdown(false);
        return;
      }

      const num = parseFloat(amount);
      if (isNaN(num) || num < 100) {
        setBreakdown(null);
        setShowProtection(false);
        setShowBreakdown(false);
        return;
      }

      setIsCalculating(true);
      setShowBreakdown(false);

      try {
        const result = await calculateSellerPayout(num, sellerId, dealId);
        setBreakdown(result);
        setShowProtection(true);
      } catch (error) {
        console.error("Failed to calculate breakdown:", error);
        setBreakdown(null);
        setShowProtection(false);
      } finally {
        setIsCalculating(false);
      }
    };

    const debounce = setTimeout(calculateBreakdown, 500);
    return () => clearTimeout(debounce);
  }, [amount, sellerId, dealId]);

  const handleProtectionComplete = () => {
    setShowBreakdown(true);
  };

  const handleSubmit = async () => {
    if (!isValidAmount || isSubmitting) {
      if (!isValidAmount) setError("Enter an amount of at least ₹100");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onSubmit(amount);
      onClose();
    } catch (error) {
      console.error("Failed to request payment:", error);
      setError(error instanceof Error ? error.message : "Failed to request payment");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Request Payment</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Amount Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Deal Amount
            </label>
            <div className="flex items-center border border-gray-300 rounded-xl px-4 py-3 bg-gray-50 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
              <span className="text-xl font-bold text-gray-600 mr-2">₹</span>
              <input
                type="number"
                min="100"
                step="0.01"
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value);
                  setError(null);
                }}
                placeholder="Enter amount"
                className="flex-1 text-2xl font-bold text-gray-900 bg-transparent outline-none"
                autoFocus
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">Minimum amount: ₹100</p>
            <p className="text-xs text-gray-500 mt-2">
              Delivery is your responsibility-include all delivery or fulfilment costs in the requested amount.
            </p>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <button
              onClick={handleSubmit}
              disabled={!isValidAmount || isSubmitting}
              className="w-full mt-4 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-semibold py-4 rounded-xl transition-colors flex items-center justify-center"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Sending Request...
                </>
              ) : (
                "Request Payment"
              )}
            </button>
          </div>

          {/* Loading State */}
          {isCalculating && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 text-blue-600 animate-spin mr-2" />
              <span className="text-sm text-gray-600">Calculating...</span>
            </div>
          )}

          {/* Protection Checklist Animation */}
          {showProtection && breakdown && !isCalculating && (
            <ProtectionChecklist
              type="seller"
              amount={breakdown.requestedAmount}
              onAnimationComplete={handleProtectionComplete}
            />
          )}

          {/* Breakdown After Animation */}
          <AnimatePresence>
            {showBreakdown && breakdown && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="space-y-4"
              >
                {/* First Deal Free Badge */}
                {breakdown.isFirstDealFree && (
                  <div className="flex items-center bg-green-50 border border-green-200 rounded-lg p-3 gap-2">
                    <Gift className="w-5 h-5 text-green-600" />
                    <div>
                      <span className="text-green-800 font-semibold text-sm">
                        First Deal Free!
                      </span>
                      <p className="text-green-700 text-xs">
                        Platform fee waived for this deal.
                      </p>
                    </div>
                  </div>
                )}

                {/* Cost Breakdown */}
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm text-slate-600">Deal Amount</span>
                    <span className="text-sm font-semibold text-slate-800">
                      ₹{formatMoney(breakdown.requestedAmount)}
                    </span>
                  </div>

                  <div className="flex justify-between items-center py-2">
                    <div className="flex items-center">
                      <span className="text-sm text-slate-600">Platform Fee</span>
                      <span className="text-xs text-gray-400 ml-1">
                        ({breakdown.isFirstDealFree
                          ? "0% - first deal free"
                          : `${breakdown.platformFeePercent.toFixed(1)}%`})
                      </span>
                    </div>
                    {breakdown.isFirstDealFree ? (
                      <span className="text-sm font-semibold text-green-600">
                        ₹0.00
                      </span>
                    ) : (
                      <span className="text-sm font-semibold text-red-500">
                        -₹{formatMoney(breakdown.platformFee)}
                      </span>
                    )}
                  </div>

                  <div className="h-px bg-slate-200 my-2" />

                  <div className="flex justify-between items-center py-2">
                    <span className="text-base font-bold text-slate-800">
                      You'll Receive
                    </span>
                    <span className="text-xl font-bold text-green-600">
                      ₹{formatMoney(breakdown.youWillReceive)}
                    </span>
                  </div>
                </div>

                {/* Info Note */}
                <div className="flex bg-gray-100 rounded-lg p-3 gap-2">
                  <Info className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-gray-600 leading-relaxed">
                    After the payer confirms delivery, your payout is sent by IMPS and
                    completes when Cashfree receives the beneficiary bank confirmation.
                  </p>
                </div>

              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}

export default SellerPaymentRequestModal;
